import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetSetQuery, useGetSetItemsQuery, useRemoveSetItemMutation, useUpdateSetMutation, useDeleteSetMutation } from '../../../../entities/sets/sets.api';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser } from '../../../../store/user/user.selector';
import { setSystemNotification } from '../../../../store/system/system.action';
import { selectChecklistYearDefault, selectChecklistManufacturerDefault, selectChecklistSportDefault } from '../../../../store/system/system.selector';
import { getCardNumberSortValue } from '../../../../utils/firebase/checklist.js';
// components
import InputTextfield from '../../../../components/input-textfield/InputTextfield';
import InputBar from '../../../../components/input-bar/InputBar.js';
import InputTags from '../../../../components/input-tags/InputTags.js';
import Title from '../../../../components/widgets/components/title/Title.js';
import PrimaryBtn from '../../../../components/btns/primary-btn/PrimaryBtn.js';
import SecondaryBtn from '../../../../components/btns/secondary-btn/SecondaryBtn.js';
import CardCountPill from '../../../../components/card-count-pill/CardCountPill.js';
import CollapsibleChecklist from '../../../../components/collapsible-checklist/CollapsibleChecklist.js';

// assets
import copyIcon from '../../../../assets/system/icons/copy-icon.png';

import {
  SetDetailContainer,
  Header,
  SetStats,
  LoadingSpinner,
  ErrorMessage,
  EmptyState,
  DetailsContainer,
  TopRow,
  SetDescription,
  DatePill,
  TagsContainer,
  TagsLabel,
  TagsList,
  TagItem,
  TagRemoveButton,
  EbaySection,
  InfoLabelContainer,
  InfoLabel,
  CopyConfirmation,
  EbayTitleInput,
  EbayDescriptionText
} from './SetDetailPage.styles';

// // Helper function to parse checklist path into individual segments
// const parseChecklistPath = (path) => {
//   if (!path) return [];

//   // Remove the leading '/checklists/' and split by '/'
//   const segments = path.replace('/checklists/', '').split('/').filter(Boolean);

//   // Only return Series (index 3), Card Type (index 4), and Category (index 5) values
//   const relevantIndices = [3, 4, 5];
//   return segments.filter((segment, index) => relevantIndices.includes(index));
// };

// // Helper function to group cards by their checklist path
// const groupCardsByChecklistPath = (cards) => {
//   const groups = {};

//   cards.forEach(card => {
//     const path = card.checklistPath || 'no-path';
//     if (!groups[path]) {
//       groups[path] = {
//         path: card.checklistPath || null,
//         cards: []
//       };
//     }
//     groups[path].cards.push(card);
//   });

//   // Convert to array and sort by path (no-path items last)
//   return Object.values(groups).sort((a, b) => {
//     if (!a.path) return 1;
//     if (!b.path) return -1;
//     return a.path.localeCompare(b.path);
//   });
// };

// Helper function to transform set items into hierarchical structure for CollapsibleChecklist
const transformSetItemsToHierarchy = (setItems) => {
  const hierarchy = {};

  setItems.forEach(item => {
    if (!item.checklistPath) return;

    // Parse the checklist path: /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
    const pathSegments = item.checklistPath.replace('/checklists/', '').split('/').filter(Boolean);

    if (pathSegments.length < 6) return; // Need at least 6 segments for full path

    const [series, cardType, cardTypeCategory] = pathSegments;

    // Build the hierarchy starting from series (skip sport and manufacturer levels)
    if (!hierarchy[series]) hierarchy[series] = {};
    if (!hierarchy[series][cardType]) hierarchy[series][cardType] = {};
    if (!hierarchy[series][cardType][cardTypeCategory]) {
      hierarchy[series][cardType][cardTypeCategory] = {};
    }

    // Add the card to the hierarchy
    const cardId = item.id || `${item.cardNumber || 'unknown'}_${Math.random()}`;
    hierarchy[series][cardType][cardTypeCategory][cardId] = {
      id: item.id,
      cardNumber: item.cardNumber || 'N/A',
      name: item.name || ['Unknown Player'],
      team: item.team || ['Unknown Team'],
      quantity: item.quantity || 1,
      cardAttributes: item.cardAttributes || [],
      variations: item.variations || [],
      checklistPath: item.checklistPath,
      isCollected: item.isCollected !== undefined ? item.isCollected : (item.variations && item.variations.length > 0)
    };
  });

  // Sort cards by card number within each category
  Object.keys(hierarchy).forEach(series => {
    Object.keys(hierarchy[series]).forEach(cardType => {
      Object.keys(hierarchy[series][cardType]).forEach(cardTypeCategory => {
        const cards = hierarchy[series][cardType][cardTypeCategory];
        // Convert to array, sort, then convert back to object
        const sortedEntries = Object.entries(cards).sort(([keyA, cardA], [keyB, cardB]) => {
          const sortValueA = getCardNumberSortValue(cardA.cardNumber);
          const sortValueB = getCardNumberSortValue(cardB.cardNumber);
          return sortValueA - sortValueB;
        });
        
        // Rebuild the object with sorted order
        const sortedCards = {};
        sortedEntries.forEach(([key, card]) => {
          sortedCards[key] = card;
        });
        
        hierarchy[series][cardType][cardTypeCategory] = sortedCards;
      });
    });
  });

  return hierarchy;
};

export default function SetDetailPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setSlug } = useParams();
  const setId = setSlug;
  const user = useSelector(selectUser);
  const [description, setDescription] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [copiedItem, setCopiedItem] = useState(null); // 'title' or 'description' or null
  const checklistSportDefault = useSelector(selectChecklistSportDefault);
  const checklistYearDefault = useSelector(selectChecklistYearDefault);
  const checklistManufacturerDefault = useSelector(selectChecklistManufacturerDefault);

  const { data: set, isLoading: setLoading, error: setError } = useGetSetQuery(
    { uid: user?.uid, setId },
    { skip: !user?.uid || !setId }
  );

  const { data: setItems = [], isLoading: itemsLoading } = useGetSetItemsQuery(
    { uid: user?.uid, setId },
    { skip: !user?.uid || !setId }
  );

  const [removeSetItem, { isLoading: isRemoving }] = useRemoveSetItemMutation();
  const [updateSet, { isLoading: isUpdating }] = useUpdateSetMutation();
  const [deleteSet, { isLoading: isDeleting }] = useDeleteSetMutation();

  // Set description, title, and tags when set data loads
  useEffect(() => {
    if (set?.description) {
      setDescription(set.description);
    }
    if (set?.title) {
      setTitle(set.title);
    }
    if (set?.tags) {
      setTags(set.tags);
    }
  }, [set?.description, set?.title, set?.tags]);

  // Clear copy confirmation after 1 second
  useEffect(() => {
    if (copiedItem) {
      const timer = setTimeout(() => {
        setCopiedItem(null);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [copiedItem]);

  const handleBackClick = () => {
    navigate('/sets');
  };

  const handleChecklistsClick = () => {
    navigate(`/checklists/${checklistSportDefault}/${checklistYearDefault}/${checklistManufacturerDefault}`);
  };

  const handleCardClick = (card, path) => {
    // Navigate to the specific card in the checklist
    if (card.checklistPath) {
      // Ensure the path is absolute (starts with /)
      let absolutePath = card.checklistPath.startsWith('/') 
        ? card.checklistPath 
        : `/${card.checklistPath}`;
      
      // Remove the card number from the end of the path if present
      // Route format: /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
      // But checklistPath may include cardNumber: /checklists/.../cardTypeCategory/cardNumber
      const pathSegments = absolutePath.split('/').filter(Boolean);
      
      // If path has more than 7 segments (checklists + 6 params + cardNumber), remove the last one
      // Expected: ['checklists', 'sport', 'year', 'manufacturer', 'series', 'cardType', 'cardTypeCategory']
      if (pathSegments.length > 7 && pathSegments[0] === 'checklists') {
        // Remove the last segment (card number)
        pathSegments.pop();
        absolutePath = '/' + pathSegments.join('/');
      }
      
      navigate(absolutePath);
    }
  };


  // const handleNavigateToCard = (checklistPath) => {
  //   if (checklistPath) {
  //     navigate(checklistPath);
  //   }
  // };

  const handleRemoveFromSet = async (itemId) => {
    if (!user?.uid || !setId) return;

    try {
      await removeSetItem({
        uid: user.uid,
        setId,
        itemId
      }).unwrap();
    } catch (error) {
      console.error('Error removing item from set:', error);
    }
  };

  if (setLoading) {
    return (
      <SetDetailContainer>
        <LoadingSpinner>Loading set...</LoadingSpinner>
      </SetDetailContainer>
    );
  }

  if (setError || !set) {
    return (
      <SetDetailContainer>
        <ErrorMessage>Error loading set or set not found</ErrorMessage>
      </SetDetailContainer>
    );
  }

  const handleEditClick = () => {
    setIsEditing(!isEditing);
  }

  const handleDeleteClick = async () => {
    if (!user?.uid || !setId) return;

    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${set?.title || 'this set'}"? This action cannot be undone and will also delete all cards in this set.`
    );

    if (!confirmed) return;

    try {
      await deleteSet({
        uid: user.uid,
        setId
      }).unwrap();

      dispatch(setSystemNotification('Set deleted successfully!'));
      navigate('/sets'); // Navigate back to sets page
    } catch (error) {
      console.error('Error deleting set:', error);
      dispatch(setSystemNotification(`Error deleting set: ${error.message || 'Unknown error'}`));
    }
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();

      if (newTag && !tags.includes(newTag)) {
        setTags(prev => [...prev, newTag]);
        setTagInput('');
      }
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(prev => prev.filter(tag => tag !== tagToRemove));
  };

  const handleSave = async () => {
    if (!user?.uid || !setId) return;

    // Validate required fields
    if (!title.trim()) {
      dispatch(setSystemNotification('Set title is required'));
      return;
    }

    try {
      await updateSet({
        uid: user.uid,
        setId,
        setData: {
          title,
          description,
          tags
        }
      }).unwrap();

      dispatch(setSystemNotification('Set updated successfully!'));
      setIsEditing(false);
      setTagInput(''); // Clear any pending tag input
    } catch (error) {
      console.error('Error updating set:', error);
      dispatch(setSystemNotification(`Error updating set: ${error.message || 'Unknown error'}`));
    }
  };

  // Generate eBay title for the set
  const generateEbayTitle = () => {
    if (!setItems || setItems.length === 0) return '';

    const titleParts = [];
    
    // Extract series from first item's checklistPath
    // Path format: /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
    let series = '';
    if (setItems.length > 0 && setItems[0].checklistPath) {
      const pathSegments = setItems[0].checklistPath.replace('/checklists/', '').split('/').filter(Boolean);
      if (pathSegments.length >= 4) {
        series = pathSegments[3]; // series is at index 3 (after sport, year, manufacturer)
      }
    }
    
    // Add series
    if (series) {
      titleParts.push(series);
    }
    
    // Add set title
    if (set?.title) {
      titleParts.push(set.title);
    }

    // Add card count
    const totalCards = setItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    if (totalCards > 0) {
      titleParts.push(`(${totalCards} cards)`);
    }

    return titleParts.join(' ');
  };

  // Generate eBay description for the set
  const generateEbayDescription = () => {
    if (!setItems || setItems.length === 0) return '';

    const parts = [];

    // Header
    parts.push('You are purchasing the exact cards pictured.\n');

    // Header
    parts.push('Order packaged with 1 bonus card.\n');

    // Set Details Section
    parts.push('<b>🏷️ Set Details</b>');
    
    // Extract series from first item's checklistPath
    // Path format: /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
    let series = '';
    if (setItems.length > 0 && setItems[0].checklistPath) {
      const pathSegments = setItems[0].checklistPath.replace('/checklists/', '').split('/').filter(Boolean);
      if (pathSegments.length >= 4) {
        series = pathSegments[3]; // series is at index 3 (after sport, year, manufacturer)
      }
    }
    
    if (series) {
      parts.push(`<b>Series:</b> ${series}`);
    }
    
    if (set?.title) {
      parts.push(`<b>Set:</b> ${set.title}`);
    }

    const totalCards = setItems.reduce((sum, item) => sum + (item.quantity || 1), 0);
    parts.push(`<b>Number of Cards:</b> ${totalCards}`);

    // Card Details Section
    parts.push('\n<b>📋 Card List:</b>');
    
    // Sort setItems by card number before grouping
    const sortedSetItems = [...setItems].sort((a, b) => {
      const sortValueA = getCardNumberSortValue(a.cardNumber);
      const sortValueB = getCardNumberSortValue(b.cardNumber);
      return sortValueA - sortValueB;
    });
    
    // Group cards by hierarchical structure (series / cardType / cardTypeCategory)
    const groupedCards = {};
    
    sortedSetItems.forEach((item) => {
      let groupKey = 'Unknown';
      
      // Extract hierarchical structure from checklist path
      // Path format: /checklists/sport/year/manufacturer/series/cardType/cardTypeCategory
      if (item.checklistPath) {
        const pathSegments = item.checklistPath.replace('/checklists/', '').split('/').filter(Boolean);
        if (pathSegments.length >= 6) {
          const series = pathSegments[3]; // series is at index 3 (after sport, year, manufacturer)
          const cardType = pathSegments[4]; // cardType is at index 4
          const cardTypeCategory = pathSegments[5]; // cardTypeCategory is at index 5
          groupKey = `${series} / ${cardType} / ${cardTypeCategory}`;
        }
      }
      
      if (!groupedCards[groupKey]) {
        groupedCards[groupKey] = [];
      }
      groupedCards[groupKey].push(item);
    });
    
    // Display grouped cards with headers
    Object.keys(groupedCards).forEach((groupKey) => {
      // Add header for the group (bold)
      parts.push(`\n<b>${groupKey}:</b>`);
      
      // List cards in this group
      groupedCards[groupKey].forEach((item) => {
        // Primary line: Card number and player name(s)
        const primaryParts = [];
        
        if (item.cardNumber) {
          primaryParts.push(`#${item.cardNumber}`);
        }

        if (item.name && Array.isArray(item.name) && item.name.length > 0) {
          primaryParts.push(item.name.join(' & '));
        }

        if (primaryParts.length > 0) {
          parts.push(primaryParts.join(' '));
        }

        // Secondary bulleted line: Variation, Team(s), Qty
        const secondaryParts = [];
        
        // Variation name (convert to CamelCase)
        if (item.variations && item.variations.length > 0) {
          const variationName = item.variations[0].cardParallelName || 'BASE';
          // Convert to CamelCase: "BASE" -> "Base", "GOLD_FOIL" -> "Gold Foil"
          const camelCaseVariation = variationName
            .split(/[_\s-]+/)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          secondaryParts.push(camelCaseVariation);
        } else {
          secondaryParts.push('Base');
        }

        // Team(s) - only add if team exists and is not empty
        if (item.team) {
          if (Array.isArray(item.team)) {
            const teams = item.team.filter(t => t && t.trim()).join(' & ');
            if (teams) {
              secondaryParts.push(teams);
            }
          } else if (item.team.trim()) {
            secondaryParts.push(item.team);
          }
        }

        // Quantity in brackets
        const qty = item.quantity || 1;
        secondaryParts.push(`Qty: ${qty}`);

        // Only add secondary line if we have at least variation and qty
        if (secondaryParts.length > 0) {
          parts.push(`  • ${secondaryParts.join(' • ')}`);
        }
      });
    });

    // Condition Section
    parts.push('\n📦 Condition');
    parts.push('Near Mint or Better. Cards pulled from pack and sleeved immediately.');
    parts.push('Please review photos carefully before purchasing, feel free to request additional photos.');

    // Shipping Section
    parts.push('\n🚚 Shipping');
    parts.push('Ships securely in penny sleeves, top loaders, and team bags.');
    parts.push('eBay Standard Envelope (for eligible cards) or BMWT for higher value items.');
    parts.push('Combined shipping available — add items to cart before checkout.');

    // Message Section
    parts.push('\n💬 Message me with any questions — happy to help.');

    return parts.join('\n');
  };

  const handleCopyTitle = () => {
    const title = generateEbayTitle();
    navigator.clipboard.writeText(title).then(() => {
      setCopiedItem('title');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };

  const handleCopyDescription = () => {
    const description = generateEbayDescription();
    navigator.clipboard.writeText(description).then(() => {
      setCopiedItem('description');
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  };


  return (
    <SetDetailContainer>
      <DetailsContainer className='widget-bg'>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", width: "100%" }}>
          <SecondaryBtn title="BACK" onClick={handleBackClick} />
          <div style={{ display: "flex", gap: "8px" }}>
            {isEditing &&
              <PrimaryBtn
                title={isDeleting ? "DELETING..." : "DELETE"}
                type="critical"
                onClick={handleDeleteClick}
                disabled={isDeleting}
              />
            }
            <PrimaryBtn
              title={isEditing ? (isUpdating ? "SAVING..." : "SAVE") : "EDIT"}
              onClick={isEditing ? handleSave : handleEditClick}
              disabled={isUpdating || isDeleting}
            />
          </div>

        </div>

        <Header>
          <TopRow>
            {isEditing ? (
              <InputBar
                type="text"
                id="set-title"
                label="Set Title"
                value={title}
                updateValue={setTitle}
              />
            ) : (
              <Title text={set.title} />
            )}
          </TopRow>

          {isEditing ?
            <InputTextfield
              id="set-description"
              label="Description"
              value={description}
              updateValue={setDescription}
              rows={2}
            />
            :
            <SetDescription>
              <h3>{set.description}</h3>
            </SetDescription>
          }

          {/* eBay Listing Title and Description */}
          {!isEditing && setItems && setItems.length > 0 && (
            <EbaySection>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <InfoLabelContainer>
                  <InfoLabel>eBay Listing Title</InfoLabel>
                  <img
                    src={copyIcon}
                    alt="Copy"
                    style={{ width: '12px', height: '12px', cursor: 'pointer' }}
                    onClick={handleCopyTitle}
                  />
                  {copiedItem === 'title' && (
                    <CopyConfirmation>Copied to clipboard!</CopyConfirmation>
                  )}
                </InfoLabelContainer>
                <EbayTitleInput
                  value={generateEbayTitle()}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <InfoLabelContainer>
                  <InfoLabel>eBay Listing Description</InfoLabel>
                  <img
                    src={copyIcon}
                    alt="Copy"
                    style={{ width: '12px', height: '12px', cursor: 'pointer' }}
                    onClick={handleCopyDescription}
                  />
                  {copiedItem === 'description' && (
                    <CopyConfirmation>Copied to clipboard!</CopyConfirmation>
                  )}
                </InfoLabelContainer>
                <EbayDescriptionText
                  value={generateEbayDescription()}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
              </div>
            </EbaySection>
          )}

          <TagsContainer>
            <TagsLabel>Tags:</TagsLabel>
            {isEditing ? (
              <>
                <InputTags
                  id="tag-input"
                  label="Add Tag"
                  value={tagInput}
                  updateValue={setTagInput}
                  onKeyDown={handleAddTag}
                />
                {tags.length > 0 && (
                  <TagsList>
                    {tags.map((tag, index) => (
                      <TagItem key={index}>
                        {tag}
                        <TagRemoveButton
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                        >
                          ×
                        </TagRemoveButton>
                      </TagItem>
                    ))}
                  </TagsList>
                )}
              </>
            ) : (
              set.tags && set.tags.length > 0 ? (
                <TagsList>
                  {set.tags.map((tag, index) => (
                    <TagItem key={index}>{tag}</TagItem>
                  ))}
                </TagsList>
              ) : (
                <span style={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '12px' }}>
                  No tags
                </span>
              )
            )}
          </TagsContainer>


          <SetStats>
            <CardCountPill value={set.itemCount || 0} />
            <DatePill>
              <h5>Created {new Date(set.createdAt).toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: '2-digit',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}</h5>
            </DatePill>
            <DatePill>
              <h5>Last Updated {new Date(set.updatedAt || set.createdAt).toLocaleString('en-US', {
                month: 'numeric',
                day: 'numeric',
                year: '2-digit',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
              })}</h5>
            </DatePill>
          </SetStats>
        </Header>

        {itemsLoading ? (
          <LoadingSpinner>Loading cards...</LoadingSpinner>
        ) : setItems.length > 0 ? (
          <CollapsibleChecklist
            data={transformSetItemsToHierarchy(setItems)}
            onCardClick={handleCardClick}
            onRemoveCard={handleRemoveFromSet}
            isRemoving={isRemoving}
          />
        ) : (
          <EmptyState>
            <h2>No cards in this set</h2>
            <PrimaryBtn
              title="+ ADD CARDS"
              onClick={handleChecklistsClick}
            />
          </EmptyState>
        )}
      </DetailsContainer>
    </SetDetailContainer>
  );
}
